import React, { useState, useEffect } from 'react'
import {Menu, X, ArrowRight, Brain, Zap, TrendingUp, BarChart3, CheckCircle, Users, Target, Shield, Sparkles, MessageSquare, FileText, Smartphone} from 'lucide-react'
import Hero from './components/Hero'
import Services from './components/Services'
import AIServices from './components/AIServices'
import About from './components/About'
import Testimonials from './components/Testimonials'
import LeadForm from './components/LeadForm'
import Contact from './components/Contact'
import Footer from './components/Footer'
import StickyCTA from './components/StickyCTA'
import ScrollProgress from './components/ScrollProgress'
import ExitIntentPopup from './components/ExitIntentPopup'
import { useEngagementTracking } from './hooks/useEngagementTracking'


function App() {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [scrolled, setScrolled] = useState(false)

  useEngagementTracking()

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50)
    }
    window.addEventListener('scroll', handleScroll)
    
    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [])

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id)
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' })
      setIsMenuOpen(false)
    }
  }

  return (
    <div className="min-h-screen bg-slate-950">
      {/* Navigation */}
      <nav className={`fixed w-full z-50 transition-all duration-300 ${scrolled ? 'bg-slate-900/95 backdrop-blur-md shadow-lg' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-br from-cyan-400 to-emerald-400 rounded-lg flex items-center justify-center">
                <Sparkles className="w-6 h-6 text-slate-900" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-white">Triangle Project Consultants</h1>
                <p className="text-xs text-cyan-400">AI-Enhanced Solutions</p>
              </div>
            </div>

            {/* Desktop Menu */}
            <div className="hidden md:flex space-x-8">
              <button onClick={() => scrollToSection('home')} className="text-gray-300 hover:text-cyan-400 transition">Home</button>
              <button onClick={() => scrollToSection('ai-services')} className="text-gray-300 hover:text-cyan-400 transition">AI Services</button>
              <button onClick={() => scrollToSection('services')} className="text-gray-300 hover:text-cyan-400 transition">Services</button>
              <button onClick={() => scrollToSection('about')} className="text-gray-300 hover:text-cyan-400 transition">About</button>
              <a href="https://ai.triangleprojectconsultants.com" target="_blank" rel="noopener noreferrer" className="text-amber-400 hover:text-amber-300 transition font-medium flex items-center gap-1">
                <Sparkles className="w-4 h-4" />
                Free AI Assessment
              </a>
              <button onClick={() => scrollToSection('contact')} className="text-gray-300 hover:text-cyan-400 transition">Contact</button>
            </div>

            <button onClick={() => scrollToSection('lead-form')} className="hidden md:block px-6 py-2.5 bg-gradient-to-r from-cyan-500 to-emerald-500 text-white rounded-lg hover:shadow-lg hover:shadow-cyan-500/50 transition">
              Get Started
            </button>

            {/* Mobile Menu Button */}
            <button onClick={() => setIsMenuOpen(!isMenuOpen)} className="md:hidden text-white">
              {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="md:hidden bg-slate-900/98 backdrop-blur-md">
            <div className="px-4 pt-2 pb-6 space-y-3">
              <button onClick={() => scrollToSection('home')} className="block w-full text-left px-4 py-2 text-gray-300 hover:text-cyan-400 hover:bg-slate-800 rounded transition">Home</button>
              <button onClick={() => scrollToSection('ai-services')} className="block w-full text-left px-4 py-2 text-gray-300 hover:text-cyan-400 hover:bg-slate-800 rounded transition">AI Services</button>
              <button onClick={() => scrollToSection('services')} className="block w-full text-left px-4 py-2 text-gray-300 hover:text-cyan-400 hover:bg-slate-800 rounded transition">Services</button>
              <button onClick={() => scrollToSection('about')} className="block w-full text-left px-4 py-2 text-gray-300 hover:text-cyan-400 hover:bg-slate-800 rounded transition">About</button>
              <a href="https://ai.triangleprojectconsultants.com" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 px-4 py-2 text-amber-400 hover:text-amber-300 hover:bg-slate-800 rounded transition font-medium">
                <Sparkles className="w-4 h-4" />
                Free AI Assessment
              </a>
              <button onClick={() => scrollToSection('contact')} className="block w-full text-left px-4 py-2 text-gray-300 hover:text-cyan-400 hover:bg-slate-800 rounded transition">Contact</button>
              <button onClick={() => scrollToSection('lead-form')} className="block w-full px-4 py-2 bg-gradient-to-r from-cyan-500 to-emerald-500 text-white rounded-lg mt-4">Get Started</button>
            </div>
          </div>
        )}
      </nav>

      {/* Main Content */}
      <main>
        <section id="home">
          <Hero onGetStarted={() => scrollToSection('lead-form')} onViewServices={() => scrollToSection('services')} />
        </section>
        
        <section id="ai-services">
          <AIServices />
        </section>
        
        <section id="services">
          <Services />
        </section>
        
        <section id="about">
          <About />
        </section>
        
        <Testimonials />
        
        <section id="lead-form">
          <LeadForm />
        </section>
        
        <section id="contact">
          <Contact />
        </section>
      </main>

      <Footer />
      <StickyCTA />
      <ScrollProgress />
      <ExitIntentPopup />
    </div>
  )
}

export default App
